import { useState, useCallback } from "react";
import { ApiDataResponse, ApiResponse, Guardian } from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useGuardiansContext } from "../../Contexts/GuardiansContext";
import { useApiResponseHandler } from "../useApiResponseHandler";
import { useStudentsContext } from "../../Contexts/StudentsContext";

export const useGuardianService = () => {
  const { setStudents, setSelectedStudent } =
    useStudentsContext();
  const { setSelectedGuardian, selectedGuardian } = useGuardiansContext();

  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const addGuardian = useCallback(
    async (
      guardian: Guardian,
      studentId: string,
      onSuccess?: (guardian: Guardian) => void
    ): Promise<{ result: ApiDataResponse<Guardian> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Guardian>(
          `/guardians/${studentId}`,
          axiosClient
        ).create({
          ...guardian,
        });
        if (result.succeeded) {
          const savedGuardian = result.data;
          setStudents((prevStudents) =>
            prevStudents.map((student) =>
              student.id === studentId
                ? {
                    ...student,
                    guardians: [...student.guardians, savedGuardian],
                  }
                : student
            )
          );

          setSelectedStudent((prevSelected) => {
            if (prevSelected && prevSelected.id === studentId) {
              return {
                ...prevSelected,
                guardians: [...prevSelected.guardians, savedGuardian],
              };
            }
            return prevSelected;
          });

          handleApiSuccess("Guardian added successfully");
          onSuccess?.(result.data);
          return { result };
        } else {
          handleApiError(result, "Failed to add guardian");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to add guardian");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to add guardian",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: {} as Guardian,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [
      axiosClient,
      setStudents,
      setSelectedStudent,
      handleApiError,
      handleApiSuccess,
      clearErrors,
    ]
  );

  const editGuardian = useCallback(
    async (
      guardian: Guardian,
      guardianId: string,
      onSuccess?: (guardian: Guardian) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Guardian>(
          "/guardians",
          axiosClient
        ).update(guardianId, guardian);
        if (result.succeeded) {
          const updatedGuardian: Guardian = {
            ...guardian,
            guardianId: guardianId, // Ensure the guardianId is preserved
          };

          const updateGuardiansList = (guardians: Guardian[]) =>
            guardians.map((g) =>
              g && g.guardianId === guardianId ? updatedGuardian : g
            );

          setStudents((prevStudents) =>
            prevStudents.map((student) => {
              if (
                student.guardians &&
                student.guardians.some((g) => g && g.guardianId === guardianId)
              ) {
                return {
                  ...student,
                  guardians: updateGuardiansList(student.guardians),
                };
              }
              return student;
            })
          );

          setSelectedStudent((prevSelected) => {
            if (
              prevSelected &&
              prevSelected.guardians &&
              prevSelected.guardians.some(
                (g) => g && g.guardianId === guardianId
              )
            ) {
              return {
                ...prevSelected,
                guardians: updateGuardiansList(prevSelected.guardians),
              };
            }
            return prevSelected;
          });

          if (selectedGuardian?.guardianId === guardianId) {
            setSelectedGuardian(updatedGuardian);
          }
          handleApiSuccess("Guardian updated successfully");
          onSuccess?.(guardian);
          return { result };
        } else {
          handleApiError(result, "Failed to update guardian");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update guardian");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update guardian",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, setStudents, setSelectedStudent, setSelectedGuardian, handleApiError, handleApiSuccess, clearErrors]
  );

  const deleteGuardian = useCallback(
    async (
      guardianId: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Guardian>(
          "/guardians",
          axiosClient
        ).delete(guardianId);
        if (result.succeeded) {
            const filterGuardians = (guardians: Guardian[]) =>
                guardians.filter((g) => g && g.guardianId !== guardianId);
      
              setStudents((prevStudents) =>
                prevStudents.map((student) => {
                  if (
                    student.guardians &&
                    student.guardians.some((g) => g && g.guardianId === guardianId)
                  ) {
                    return {
                      ...student,
                      guardians: filterGuardians(student.guardians),
                    };
                  }
                  return student;
                })
              );
      
              setSelectedStudent((prevSelected) => {
                if (
                  prevSelected &&
                  prevSelected.guardians &&
                  prevSelected.guardians.some((g) => g && g.guardianId === guardianId)
                ) {
                  return {
                    ...prevSelected,
                    guardians: filterGuardians(prevSelected.guardians),
                  };
                }
                return prevSelected;
              });
      
              if (selectedGuardian?.guardianId === guardianId) {
                setSelectedGuardian(null);
              }
          handleApiSuccess("Guardian deleted successfully");
          onSuccess?.(guardianId);
          return { result };
        } else {
          handleApiError(result, "Failed to delete guardian");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete guardian");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete guardian",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, setStudents, setSelectedStudent, setSelectedGuardian, handleApiError, handleApiSuccess, clearErrors]
  );

  return {
    addGuardian,
    editGuardian,
    deleteGuardian,
    isLoading,
    errors,
    clearErrors,
  };
};
